import { STATIC_VALUE_SOURCE, isComparableOperator } from '../../codecs/flat-comparison-filter';
import type { OperatorValuePatch, ValueOption } from './types';

/**
 * What a comparison's value can be read from, and what each one is called.
 *
 * The labels are lower case because the select reads inside a sentence:
 * "plan is equal to [the current person's attribute] plan".
 */
const VALUE_SOURCES = {
  [STATIC_VALUE_SOURCE]: { label: 'the value', placeholder: undefined },
  customer_attribute: { label: "the current person's attribute", placeholder: 'Profile attribute' },
} as const satisfies Record<string, { label: string; placeholder: string | undefined }>;

/** A value source a surface can declare. */
export type ValueSource = keyof typeof VALUE_SOURCES;

/** The select's options for the sources a surface declares, in the order it declares them. */
export function valueSourceOptions(declared: readonly ValueSource[]): ValueOption[] {
  return declared.map((source) => ({ value: source, label: VALUE_SOURCES[source].label }));
}

/**
 * What the value field says while empty, for a value that names an attribute
 * rather than holding a literal. Undefined for the literal, which keeps the
 * editor's own placeholder.
 */
export function valueSourcePlaceholder(valueSource: string | undefined): string | undefined {
  if (valueSource === 'customer_attribute') {
    return VALUE_SOURCES.customer_attribute.placeholder;
  }
  return undefined;
}

/** Whether the row shows its value-source select: the surface offers a choice and the operator can compare. */
export function showsValueSource(operator: string, options: readonly ValueOption[]): boolean {
  return options.length > 0 && isComparableOperator(operator);
}

/**
 * The source change an operator change brings with it.
 *
 * An operator that cannot compare drops the source back to the literal. Without
 * that, changing back to an operator that can compare would bring back a
 * source the user left behind.
 */
export function valueSourcePatchForOperator(
  operator: string,
  valueSource: string | undefined,
  options: readonly ValueOption[] | undefined,
): OperatorValuePatch {
  if (options == null || valueSource == null || valueSource === STATIC_VALUE_SOURCE) {
    return {};
  }
  return isComparableOperator(operator) ? {} : { valueSource: STATIC_VALUE_SOURCE };
}

/**
 * The patch a pick in the value-source select writes, or null for no change.
 *
 * A move between two attribute sources clears the value, because the name of
 * one kind of attribute is not the name of another. A move into or out of the
 * literal keeps it.
 */
export function valueSourceChangePatch(current: string | undefined, next: string | null): OperatorValuePatch | null {
  const from = current ?? STATIC_VALUE_SOURCE;
  if (next == null || next === from) {
    return null;
  }
  if (from !== STATIC_VALUE_SOURCE && next !== STATIC_VALUE_SOURCE) {
    return { valueSource: next, value: '' };
  }
  return { valueSource: next };
}
